import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import Role from "enums/Role";
import UserStatus from "enums/UserStatus";
import { useFormik } from "formik";
import {
  GET_USER,
  GET_USER_BY_ID,
  GET_USER_BY_EMAIL,
  UPDATE_USER,
  ADD_USER,
} from "graphql/user";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useGetLoggedInUser from "./useGetLoggedInUser";
import { FormikAddress } from "utils/addressFormat";
import { useUserMentions } from "context/UserMentionProvider";

export interface User {
  id: string;
  departmentId: string;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  birthdate: string;
  address: FormikAddress | null;
  identifyingId?: string;
  identifyingType?: string;
  issuingCountry?: string;
  isOwner?: boolean;
  role?: Role;
  status?: UserStatus;
  isValidated?: boolean;
  location?: { id: string };
  // UI-only attestation on the Primary Authorized Person step. Not persisted.
  authorizationConfirmed?: boolean;
}

// The user queries return the department as an object, not the id a User carries.
export type UserNode = Omit<User, "departmentId"> & {
  department?: { id: string } | null;
};

export interface UsersQueryData {
  users: { edges: { node: UserNode }[] };
}

export const userFromNode = ({ department, ...node }: UserNode): User => ({
  ...node,
  departmentId: department?.id ?? "",
});

interface UseAddUpdateUserOptions {
  initialValues?: any;
  staticValues?: any;
  validationSchema: any;
  onCompleted?: (res: any) => void;
  onError?: (err: any) => void;
  id?: string;
  userId?: string;
  email?: string;
  isOwner?: boolean;
  handleCloseDrawer?: (refresh?: boolean) => void;
}

const useAddUpdateUser = ({
  initialValues,
  staticValues,
  validationSchema,
  onCompleted,
  onError,
  id,
  userId,
  email,
  handleCloseDrawer,
}: UseAddUpdateUserOptions) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { user: loggedInUser } = useGetLoggedInUser();
  const { refetch: refetchGlobalUsers } = useUserMentions();

  const [updateUser, { loading: updatingUser }] = useMutation(UPDATE_USER);
  const [addUser, { loading: addingUser }] = useMutation(ADD_USER);

  const formik = useFormik<User>({
    initialValues: {
      id: "",
      departmentId: "",
      firstName: "",
      lastName: "",
      title: "",
      email: "",
      phone: "",
      birthdate: "",
      identifyingId: "",
      identifyingType: "",
      issuingCountry: "",
      isOwner: false,
      role: Role.Employee,
      status: UserStatus.PENDING,
      address: {
        addressName: "Default",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "US",
      },
      location: { id: "" },
      ...(initialValues || {}),
      ...(staticValues || {}),
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      try {
        if (staticValues) {
          values = {
            ...values,
            ...staticValues,
          };
        }
        if (!values.departmentId) {
          values.departmentId = loggedInUser?.departmentId;
        }
        if (!values.address?.country) {
          values.address.country = "US";
        }
        if (!values.address?.addressName) {
          values.address.addressName = "Default";
        }
        delete values.address?.geocode;
        if (values.id) {
          await updateUser({
            variables: {
              id: values.id,
              input: {
                departmentId: values.departmentId,
                firstName: values.firstName,
                lastName: values.lastName,
                title: values.title,
                email: values.email,
                phone: values.phone,
                birthdate: dayjs(values.birthdate).valueOf(),
                address: values.address,
                isOwner: values.isOwner,
                status: values.status,
                role: values.role,
                location: values.location,
              },
            },
          });
        } else {
          await addUser({
            variables: {
              input: {
                departmentId: values.departmentId,
                firstName: values.firstName,
                lastName: values.lastName,
                title: values.title,
                email: values.email,
                phone: values.phone,
                birthdate: dayjs(values.birthdate).valueOf(),
                address: values.address,
                role: values.role,
                isOwner: values.isOwner,
                status: values.status,
                location: values.location,
              },
            },
          });
        }
        onCompleted && onCompleted(values);
        handleCloseDrawer && handleCloseDrawer(true);
        refetchGlobalUsers();
      } catch (err) {
        onError && onError(err);
      }
    },
  });

  const [
    fetchUserById,
    { data: userIdData, loading: loadingUserId, refetch: refetchUserId },
  ] = useLazyQuery(GET_USER_BY_ID, {
    fetchPolicy: "network-only",
  });

  const [
    fetchUser,
    { data: userData, loading: loadingUser, refetch: refetchUser },
  ] = useLazyQuery(GET_USER, {
    fetchPolicy: "network-only",
  });

  const [
    fetchUserByEmail,
    {
      data: userEmailData,
      loading: loadingUserEmail,
      refetch: refetchUserEmail,
    },
  ] = useLazyQuery(GET_USER_BY_EMAIL, {
    fetchPolicy: "network-only",
  });

  // Helper to pick only allowed fields from an Address object.
  function pickAddressFields(address: any): FormikAddress {
    return {
      addressName: address?.addressName || "Default",
      address1: address?.address1 || "",
      address2: address?.address2 || "",
      city: address?.city || "",
      state: address?.state || "",
      zipcode: address?.zipcode || "",
      country: address?.country || "US",
    };
  }

  // Helper to pick only allowed fields from a User object.
  function pickUserFields(values: any): User {
    return {
      id: values.id,
      departmentId: values.departmentId,
      firstName: values.firstName,
      lastName: values.lastName,
      title: values.title,
      email: values.email,
      phone: values.phone,
      birthdate: values.birthdate
        ? dayjs(values.birthdate).format("YYYY-MM-DD")
        : "",
      address: pickAddressFields(values.address),
      identifyingId: values.identifyingId,
      identifyingType: values.identifyingType || "SSN",
      issuingCountry: values.issuingCountry || "US",
      isOwner: values.isOwner,
      role: values.role,
      status: values.status,
      location: { id: values.location?.id },
    };
  }

  // Centralized function to update user values in formik.
  async function setUser(values: any) {
    const updatedUser = pickUserFields(values);
    await formik.setValues({
      ...formik.values,
      ...updatedUser,
    });
  }

  useEffect(() => {
    if (userData || userIdData || userEmailData) {
      const u =
        userData?.user || userIdData?.userById || userEmailData?.userByEmail;
      setUser({
        id: u.id,
        departmentId: u?.department?.id,
        firstName: u?.firstName,
        lastName: u?.lastName ?? "",
        title: u?.title,
        email: u?.email,
        phone: u?.phone,
        birthdate: dayjs(u?.birthdate).format("YYYY-MM-DD"),
        address: {
          addressName:
            u?.address?.addressName ||
            formik.initialValues.address?.addressName,
          address1: u?.address?.address1,
          address2: u?.address?.address2,
          city: u?.address?.city,
          state: u?.address?.state,
          zipcode: u?.address?.zipcode,
          country: u?.address?.country || formik.initialValues.address?.country,
        },
        isOwner: u?.isOwner || false,
        status: u?.status || UserStatus.PENDING,
        role: u?.role || Role.Employee,
      });
    }
  }, [userData, userEmailData, userIdData]);

  useEffect(() => {
    if (formik.values.id || loading) return;
    if (id) {
      fetchUserById({ variables: { id } });
    } else if (userId) {
      fetchUser({ variables: { userId } });
    } else if (email) {
      fetchUserByEmail({ variables: { email } });
    }
  }, [id, userId, email, formik.values.email]);

  useEffect(() => {
    setLoading(
      loadingUser ||
        loadingUserId ||
        loadingUserEmail ||
        updatingUser ||
        addingUser,
    );
  }, [loadingUser, loadingUserId, loadingUserEmail, updatingUser, addingUser]);

  return { formik, setUser, loading };
};

export default useAddUpdateUser;
